import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { FC, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { IdNameList } from "../../../../../../api/services/common/models";
import { Language } from "../../../../../../api/services/pageContent/models";
import { useGetPageP1ContentMutation } from "../../../../../../api/services/pageContent/pageContentApi";
import { useGetParticipantDetailsById } from "../../../../../../api/services/program/programApis";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiButtonTertiary,
} from "../../../../../../components";
import PermissionWrapper from "../../../../../../components/permissionWrapper/PermissionWrapper";
import { CountryField } from "../../../../../../components/textFieldWithValidation/TextFieldWithValidation";
import {
  MODULE_PERMISSIONS,
  TM_INDIVIDUAL_ASSESSMENT,
  TM_LEADER_COACHING,
} from "../../../../../../helpers/constants";
import { COMPONENT_IDS } from "../../../../../../helpers/constants/componentIds";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";
import { commonSiteCoreKeys } from "../../../../../../helpers/constants/siteCoreKeys";
import {
  calculateAge,
  convertToUserFormat,
  getIdsAsStringArray,
  getValueOrBlank,
  getValueOrBlankArray,
  isNotEmpty,
} from "../../../../../../helpers/utils";
import useAccordion from "../../../../../../hooks/useAccordion";
import { usePermissions } from "../../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../redux/slices/appState/appStateSlice";
import { programStatusSelector } from "../../../../../../redux/slices/program/programSelector";
import { getSelectedEmail } from "../../../../../participants/helper";
import {
  ParticipantSectionKey,
  ParticipantTabFormKey,
  ParticipantTabFormType,
  ProgramDetail,
} from "../../../../modelsAndSchemas/Models";
import {
  activityTimelineSiteCoreKeys,
  programSiteCoreKeys,
} from "../../../../siteCore/SiteCoreKeys";
import { RegistrationDetail } from "../../../createProgramGeneralDetail/inputDetailTab/registrationDetail/RegistrationDetail";
import { AboutParticipant } from "./components/aboutParticipant/AboutParticipant";
import { CoachAssignment } from "./components/coachAssignment/coachAssignment";
import { CoachAssignmentFormKeys } from "./components/coachAssignment/constants";
import { coachAssignmentSiteCoreKeys } from "./components/coachAssignment/SiteCoreKeys";
import ParticipantActivityTimeline from "./components/participantActivityTimeline/ParticipantActivityTimeline";
import { ParticipantAddressDetails } from "./components/participantAddressDetails/ParticipantAddressDetails";
import { ParticipantCareerGoal } from "./components/participantCareerGoal/ParticipantCareerGoal";
import { ParticipantContactAndNotification } from "./components/participantContactAndNotification/ParticipantContactAndNotification";
import { GoalSelection } from "./components/participantGoalSelection/GoalSelection";
import { goalSelectionSiteCoreKeys } from "./components/participantGoalSelection/SiteCoreKeys";
import { ParticipantInformation } from "./components/participantInformation/ParticipantInformation";
import { ParticipantIntakeSelection } from "./components/participantIntakeSelection/ParticipantIntakeSelection";
import { ParticipantOutreach } from "./components/participantOutreach/ParticipantOutreach";
import { ParticipantSponsoringEmployer } from "./components/participantSponsoringEmployer/ParticipantSponsoringEmployer";
import {
  getIsPrimaryEmail,
  getSelectedEmailNotifications,
  getSmsNotification,
  getSmsNotificationOption,
} from "./NextInfoQuestion.util";

interface ParticipantTabProps {
  pageId: string;
  programDetail?: ProgramDetail;
  programId: string;
  isEditProgram?: boolean;
  isFromCoachPortal?: boolean;
  onParentSubmitPressed?: boolean;
}

const initialAccordionStatus = {
  registrationDetails: true,
  participantInformation: true,
  addressDetails: false,
  outreach: false,
  sponsoringEmployer: false,
  participantActivityTimeline: false,
  all: false,
  careerGoal: false,
  coachAssignment: false,
  goalSelection: false,
  aboutParticipant: false,
  contactAndNotification: false,
  intakeSelection: false,
};

export const ParticipantTab: FC<ParticipantTabProps> = ({
  pageId,
  programDetail,
  isEditProgram = false,
  programId,
  isFromCoachPortal = false,
  onParentSubmitPressed,
}) => {
  const {
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
    handleAccordionExpansionState,
  } = useAccordion(initialAccordionStatus);

  const [countryVatPostalData, setCountryVatPostalData] = useState<
    CountryField[]
  >([]);
  const dispatch = useDispatch();

  const { loggedInUser } = useGetLoggedInUserDetails();
  const { hasModulePermission } = usePermissions();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const programStatus = useSelector(programStatusSelector);
  const [isDataSet, setIsDataSet] = useState<boolean>(!isEditProgram);

  const isTMLeaderCoachingProgram = useMemo(() => {
    return programDetail?.tmProgramType === TM_LEADER_COACHING;
  }, [programDetail?.tmProgramType]);

  const {
    setValue,
    reset: resetParticipant,
    formState: { errors },
    getValues: participantGetValues,
  } = useFormContext();

  const { nextEmail } = participantGetValues();

  const [participantValues, setParticipantValues] =
    useState<ParticipantTabFormType>();

  useEffect(() => {
    if (programId && isEditProgram) {
      getParticipantDetailsByProgramId(programId);
    }
  }, [programId, isEditProgram, programStatus]);

  const pageContent = () => {
    getPageContent({
      PageName: "OnboardingPages",
      MarketName: getValueOrBlank(editParticipantData?.marketName),
      language: editParticipantData?.preferredLanguage,
      Persona: getValueOrBlank(programDetail?.nextPersona),
    });
  };

  const {
    data: editParticipantData,
    isFetching: isPending,
    isError: isProgramError,
    refetchWithParams: getParticipantDetailsByProgramId,
    isSuccess: isProgramSuccess,
  } = useGetParticipantDetailsById();

  const {
    mutate: getPageContent,
    data: pageContentData,
    isPending: isPageContentPending,
    isError,
  } = useGetPageP1ContentMutation();

  const { fetchComponentTitle, commonLabels, fetchRichTexts, fetchLabel } =
    useSiteCore();

  const countryVatPostalDataJson = fetchRichTexts(
    commonSiteCoreKeys.countryBusinessRuleListParent,
    commonSiteCoreKeys.countryBusinessRuleList,
  );

  useEffect(() => {
    dispatch(
      toggleLoader({
        pendingLoader: {
          isVisible: isPending || isPageContentPending,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ pendingLoader: { isVisible: false } }));
    };
  }, [isPending, isPageContentPending]);

  useEffect(() => {
    if (isProgramError || isError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isProgramError, isError]);

  useEffect(() => {
    if (
      isEditProgram &&
      isProgramSuccess &&
      editParticipantData?.isRegisteredInNext
    ) {
      pageContent();
    }
  }, [
    isEditProgram,
    isProgramSuccess,
    editParticipantData?.isRegisteredInNext,
  ]);

  useEffect(() => {
    if (countryVatPostalDataJson) {
      const countryVatPostalDataJsonParsed: CountryField[] = JSON.parse(
        countryVatPostalDataJson,
      );
      setCountryVatPostalData(countryVatPostalDataJsonParsed);
    }
  }, [countryVatPostalDataJson]);

  const renderHeader = (labelKey: string) => {
    return <AccordionHeaderText label={fetchComponentTitle(labelKey)} />;
  };

  const getRemoveIcon = () => {
    return <RemoveIcon color={"primary"} />;
  };

  const getAddIcon = () => {
    return <AddIcon color={"primary"} />;
  };

  const smsNumber =
    editParticipantData?.nextNotificationPreferences?.smsNotification?.[0]
      ?.number;

  const isEnabledSms =
    editParticipantData?.nextNotificationPreferences?.isSMSNotificationEnabled;

  const smsOptions = getSmsNotificationOption(
    !!isEnabledSms,
    !!editParticipantData?.nextNotificationPreferences
      ?.isEmailNotificationEnabled,
    smsNumber,
  );

  const selectedEmailOptions = getSelectedEmailNotifications(
    editParticipantData?.nextNotificationPreferences?.emailNotification,
  );

  const emailNotifications = getValueOrBlankArray(
    editParticipantData?.nextNotificationPreferences?.emailNotification,
  );

  const isPrimaryEmail = getIsPrimaryEmail(nextEmail, emailNotifications);

  useEffect(() => {
    if (isPrimaryEmail !== undefined) {
      setValue(ParticipantTabFormKey.isPrimaryEmail, isPrimaryEmail);
    }
  }, [isPrimaryEmail]);

  const languageOptions =
    pageContentData?.MarketDetails?.flatMap(
      (market: { Supportedlanguage: Language[] }) =>
        market.Supportedlanguage.map((lang) => ({
          value: lang.ISOCode,
          label: lang.LanguageName,
        })),
    ).filter(
      (item: { value: string; label: string }) => item.value && item.label,
    ) || [];

  const sectionFieldsMap: Record<ParticipantSectionKey, string[]> = {
    participantInformation: [
      ParticipantTabFormKey.prefix,
      ParticipantTabFormKey.firstName,
      ParticipantTabFormKey.lastName,
      ParticipantTabFormKey.preferredName,
      ParticipantTabFormKey.dateOfBirth,
      ParticipantTabFormKey.gender,
      ParticipantTabFormKey.spouse,
      ParticipantTabFormKey.ageRange,
      ParticipantTabFormKey.educationLevel,
      ParticipantTabFormKey.mustStartByDate,
      ParticipantTabFormKey.primaryLanguage,
      ParticipantTabFormKey.socialFund,
      ParticipantTabFormKey.nisId,
      ParticipantTabFormKey.projectedProgramEndDate,
      ParticipantTabFormKey.textSubscribedDate,
      ParticipantTabFormKey.textUnsubscribedDate,
      ParticipantTabFormKey.emailUnsubscribedDate,
      ParticipantTabFormKey.preferredLanguage,
      ParticipantTabFormKey.personalId,
      ParticipantTabFormKey.batchTypeKey,
      ParticipantTabFormKey.waveCohort,
    ],

    addressDetails: [
      ParticipantTabFormKey.address1,
      ParticipantTabFormKey.address2,
      ParticipantTabFormKey.address3,
      ParticipantTabFormKey.countryName,
      ParticipantTabFormKey.city,
      ParticipantTabFormKey.stateProvince,
      ParticipantTabFormKey.postalCode,
    ],
    outreach: [
      ParticipantTabFormKey.eligibility,
      ParticipantTabFormKey.outreachEligibleStatus,
      ParticipantTabFormKey.assignedOutreachConsultant,
      ParticipantTabFormKey.primaryManagedStatus,
      ParticipantTabFormKey.secondaryManagedStatus,
      ParticipantTabFormKey.notificationDate,
      ParticipantTabFormKey.releaseDate,
      ParticipantTabFormKey.outreachEligibleDate,
      ParticipantTabFormKey.notificationSupportDate,
      ParticipantTabFormKey.notificationSupportStatus,
      ParticipantTabFormKey.contactWithin24Hours,
    ],
    sponsoringEmployer: [
      ParticipantTabFormKey.clientName,
      ParticipantTabFormKey.jobTitle,
      ParticipantTabFormKey.employeeId,
      ParticipantTabFormKey.occupationalFunction,
      ParticipantTabFormKey.occupationalFunctionOther,
      ParticipantTabFormKey.occupationalLevel,
      ParticipantTabFormKey.occupationalLevelOther,
      ParticipantTabFormKey.planPostedDate,
      ParticipantTabFormKey.datePlanApproved,
      ParticipantTabFormKey.currentEmploymentStartDate,
      ParticipantTabFormKey.bestMatchPreference,
      ParticipantTabFormKey.participantGroupType,
      ParticipantTabFormKey.socialFund,
      ParticipantTabFormKey.totalYearsEmployed,
    ],
    careerGoals: [
      ParticipantTabFormKey.targetJobTitle,
      ParticipantTabFormKey.targetOccupationFunctionId,
      ParticipantTabFormKey.targetOccupationOtherFunction,
      ParticipantTabFormKey.targetOccupationLevelId,
      ParticipantTabFormKey.targetIndustryId,
      ParticipantTabFormKey.targetOrganisation,
      ParticipantTabFormKey.targetJobTypeId,
      ParticipantTabFormKey.targetLocation,
      ParticipantTabFormKey.currenciesId,
      ParticipantTabFormKey.salaryRangeId,
      ParticipantTabFormKey.skills,
      ParticipantTabFormKey.technicalSkills,
      ParticipantTabFormKey.participantNotSeekingEmployment,
      ParticipantTabFormKey.participantNotWillngToWorkRemotely,
      ParticipantTabFormKey.openToRelocation,
      ParticipantTabFormKey.participantWillingToTravelForWork,
      ParticipantTabFormKey.experis,
      ParticipantTabFormKey.manpowerEngineering,
      ParticipantTabFormKey.jeffersonWells,
      ParticipantTabFormKey.rpo,
      ParticipantTabFormKey.participantReadyToStartJobSearch,
      ParticipantTabFormKey.participantJobSearchStartDate,
    ],
    participantActivityTimeline: [ParticipantTabFormKey.nextContactDate],
  };

  const hasErrors = (section: ParticipantSectionKey) => {
    const fields = sectionFieldsMap[section];
    return fields.some((field) => errors[field]);
  };

  useEffect(() => {
    (Object.keys(sectionFieldsMap) as ParticipantSectionKey[]).forEach(
      (sectionKey: ParticipantSectionKey) => {
        if (hasErrors(sectionKey)) {
          handleAccordionExpansionState(sectionKey.toString(), true);
        }
      },
    );
  }, [JSON.stringify(errors)]);

  const formatDate = (date: string | undefined): string => {
    return date ? convertToUserFormat(date, userCountryDateFormat) : "";
  };

  const calculateAgeIfPossible = (dateOfBirth: string): string => {
    return dateOfBirth && userCountryDateFormat
      ? calculateAge(dateOfBirth, userCountryDateFormat).toString()
      : "";
  };

  useEffect(() => {
    if (isNotEmpty(editParticipantData)) {
      const dateOfBirth = formatDate(editParticipantData?.dateOfBirth);
      const age = calculateAgeIfPossible(dateOfBirth);
      const mustStartByDate = formatDate(editParticipantData?.mustStartByDate);
      const projectedProgramEndDate = formatDate(
        editParticipantData?.projectedProgramEndDate,
      );
      const textSubscribedDate = formatDate(
        editParticipantData?.textSubscribedDate,
      );
      const textUnsubscribedDate = formatDate(
        editParticipantData?.textUnsubscribeDate,
      );
      const emailUnsubscribedDate = formatDate(
        editParticipantData?.emailUnsubscribeDate,
      );
      const releaseDate = formatDate(editParticipantData?.releaseDate);
      const notificationDate = formatDate(
        editParticipantData?.notificationDate,
      );
      const outreachEligibleDate = formatDate(
        editParticipantData?.outreachEligibleDate,
      );
      const notificationSupportDate = formatDate(
        editParticipantData?.notificationSupportDate,
      );
      const planPostedDate = formatDate(editParticipantData?.planPostedDate);
      const datePlanApproved = formatDate(
        editParticipantData?.datePlanApproved,
      );
      const currentEmploymentStartDate = formatDate(
        editParticipantData?.currentEmploymentStartDate,
      );

      const coachToBeAssignCoach = formatDate(
        editParticipantData?.coachAssignment?.toBeAssignedDate,
      );

      const actualStartDate = formatDate(editParticipantData?.actualStartDate);

      const defaultParticipantValues = {
        [ParticipantTabFormKey.prefix]: getValueOrBlank(
          editParticipantData?.prefixKey,
        ),
        [ParticipantTabFormKey.firstName]: getValueOrBlank(
          editParticipantData?.firstName,
        ),
        [ParticipantTabFormKey.lastName]: getValueOrBlank(
          editParticipantData?.lastName,
        ),
        [ParticipantTabFormKey.preferredName]: getValueOrBlank(
          editParticipantData?.preferredName,
        ),
        [ParticipantTabFormKey.dateOfBirth]: dateOfBirth,
        [ParticipantTabFormKey.age]: age,
        [ParticipantTabFormKey.ageRange]: getValueOrBlank(
          editParticipantData?.ageRange,
        ),
        [ParticipantTabFormKey.personalId]: getValueOrBlank(
          editParticipantData?.personalId,
        ),
        [ParticipantTabFormKey.gender]: getValueOrBlank(
          editParticipantData?.genderKey,
        ),
        [ParticipantTabFormKey.preferredLanguage]: getValueOrBlank(
          editParticipantData?.preferredLanguage,
        ),
        [ParticipantTabFormKey.spouse]: getValueOrBlank(
          editParticipantData?.spouse,
        ),
        [ParticipantTabFormKey.educationLevel]: getValueOrBlank(
          editParticipantData?.educationLevelKey,
        ),
        [ParticipantTabFormKey.mustStartByDate]: mustStartByDate,
        [ParticipantTabFormKey.actualStartDate]: actualStartDate,
        [ParticipantTabFormKey.projectedProgramEndDate]:
          projectedProgramEndDate,
        [ParticipantTabFormKey.textSubscribedDate]: textSubscribedDate,
        [ParticipantTabFormKey.textUnsubscribedDate]: textUnsubscribedDate,
        [ParticipantTabFormKey.emailUnsubscribedDate]: emailUnsubscribedDate,
        [ParticipantTabFormKey.primaryLanguage]: getValueOrBlank(
          editParticipantData?.primaryLanguageKey,
        ),
        [ParticipantTabFormKey.nisId]: getValueOrBlank(
          editParticipantData?.nisid,
        ),
        [ParticipantTabFormKey.nextContactDate]: formatDate(
          editParticipantData?.nextContactDate,
        ),

        [ParticipantTabFormKey.address1]: getValueOrBlank(
          editParticipantData?.address1,
        ),
        [ParticipantTabFormKey.address2]: getValueOrBlank(
          editParticipantData?.address2,
        ),
        [ParticipantTabFormKey.address3]: getValueOrBlank(
          editParticipantData?.address3,
        ),
        [ParticipantTabFormKey.countryId]: getValueOrBlank(
          editParticipantData?.countryId,
        ),
        [ParticipantTabFormKey.countryName]: getValueOrBlank(
          editParticipantData?.countryName ?? editParticipantData?.countryId,
        ),
        [ParticipantTabFormKey.city]: getValueOrBlank(
          editParticipantData?.city,
        ),
        [ParticipantTabFormKey.stateProvince]: getValueOrBlank(
          editParticipantData?.stateId,
        ),
        [ParticipantTabFormKey.postalCode]: getValueOrBlank(
          editParticipantData?.postalCode,
        ),

        [ParticipantTabFormKey.eligibility]: getValueOrBlank(
          editParticipantData?.eligibilityKey,
        ),
        [ParticipantTabFormKey.outreachEligibleStatus]: getValueOrBlank(
          editParticipantData?.outreachEligibleStatus,
        ),
        [ParticipantTabFormKey.assignedOutreachConsultant]: getValueOrBlank(
          editParticipantData?.assignedOutreachConsultant,
        ),
        [ParticipantTabFormKey.notificationDate]: notificationDate,
        [ParticipantTabFormKey.releaseDate]: releaseDate,
        [ParticipantTabFormKey.outreachEligibleDate]: outreachEligibleDate,
        [ParticipantTabFormKey.notificationSupportDate]:
          notificationSupportDate,
        [ParticipantTabFormKey.notificationSupportStatus]: getValueOrBlank(
          editParticipantData?.notificationSupportStatus,
        ),
        [ParticipantTabFormKey.primaryManagedStatus]: getValueOrBlank(
          editParticipantData?.primaryManagedStatus,
        ),
        [ParticipantTabFormKey.secondaryManagedStatus]: getValueOrBlank(
          editParticipantData?.secondaryManagedStatus,
        ),
        [ParticipantTabFormKey.contactWithin24Hours]:
          editParticipantData?.isContactInADayEnable ?? false,

        [ParticipantTabFormKey.clientName]: getValueOrBlank(
          programDetail?.clientName,
        ),
        [ParticipantTabFormKey.jobTitle]: getValueOrBlank(
          editParticipantData?.jobTitle,
        ),
        [ParticipantTabFormKey.employeeId]: getValueOrBlank(
          editParticipantData?.employeeId,
        ),
        [ParticipantTabFormKey.employeeBand]: getValueOrBlank(
          editParticipantData?.employeeBand,
        ),
        [ParticipantTabFormKey.totalYearsEmployed]: getValueOrBlank(
          editParticipantData?.totalYearsEmployed,
        ),
        [ParticipantTabFormKey.occupationalFunction]: getValueOrBlank(
          editParticipantData?.occupationalFunction,
        ),
        [ParticipantTabFormKey.occupationalFunctionOther]: getValueOrBlank(
          editParticipantData?.occupationalFunctionOther,
        ),
        [ParticipantTabFormKey.occupationalLevel]: getValueOrBlank(
          editParticipantData?.occupationalLevel,
        ),
        [ParticipantTabFormKey.occupationalLevelOther]: getValueOrBlank(
          editParticipantData?.occupationalLevelOther,
        ),

        [ParticipantTabFormKey.planPostedDate]: planPostedDate,
        [ParticipantTabFormKey.datePlanApproved]: datePlanApproved,
        [ParticipantTabFormKey.currentEmploymentStartDate]:
          currentEmploymentStartDate,
        [ParticipantTabFormKey.bestMatchPreference]: getValueOrBlank(
          editParticipantData?.bestMatchPreferenceId,
        ),
        [ParticipantTabFormKey.socialFund]: getValueOrBlank(
          editParticipantData?.socialFund,
        ),
        [ParticipantTabFormKey.participantGroupType]: getValueOrBlank(
          editParticipantData?.participantGroupType,
        ),
        [ParticipantTabFormKey.targetJobTitle]: getValueOrBlank(
          editParticipantData?.targetJobTitle,
        ),
        [ParticipantTabFormKey.targetOccupationFunctionId]: getValueOrBlank(
          editParticipantData?.targetOccupationFunctionId,
        ),
        [ParticipantTabFormKey.targetOccupationOtherFunction]: getValueOrBlank(
          editParticipantData?.targetOccupationOtherFunction,
        ),
        [ParticipantTabFormKey.targetOccupationLevelId]: getValueOrBlank(
          editParticipantData?.targetOccupationLevelId,
        ),
        [ParticipantTabFormKey.targetIndustryId]: getValueOrBlank(
          editParticipantData?.targetIndustryId,
        ),
        [ParticipantTabFormKey.targetOrganisation]: getValueOrBlank(
          editParticipantData?.targetOrganisation,
        ),
        [ParticipantTabFormKey.targetJobTypeId]: getValueOrBlank(
          editParticipantData?.targetJobTypeId,
        ),
        [ParticipantTabFormKey.targetLocation]: getValueOrBlank(
          editParticipantData?.targetLocation,
        ),
        [ParticipantTabFormKey.currenciesId]: getValueOrBlank(
          editParticipantData?.currenciesId,
        ),
        [ParticipantTabFormKey.salaryRangeId]: getValueOrBlank(
          editParticipantData?.salaryRangeId,
        ),
        [ParticipantTabFormKey.skills]: editParticipantData?.skills?.map(
          (item: IdNameList) => item.id,
        ),
        [ParticipantTabFormKey.technicalSkills]:
          editParticipantData?.technicalSkills?.map(
            (item: IdNameList) => item.id,
          ),
        [ParticipantTabFormKey.participantReadyToStartJobSearch]:
          editParticipantData?.isMarketReady === true
            ? programSiteCoreKeys.participantStartJobSearchYes
            : programSiteCoreKeys.participantStartJobSearchNo,
        [ParticipantTabFormKey.participantJobSearchStartDate]:
          editParticipantData?.marketReadinessDate
            ? formatDate(editParticipantData?.marketReadinessDate)
            : "",
        [ParticipantTabFormKey.participantNotSeekingEmployment]:
          editParticipantData?.participantNotSeekingEmployment ?? false,
        [ParticipantTabFormKey.participantNotWillngToWorkRemotely]:
          editParticipantData?.participantNotWillngToWorkRemotely ?? false,
        [ParticipantTabFormKey.openToRelocation]:
          editParticipantData?.openToRelocation ?? false,
        [ParticipantTabFormKey.participantWillingToTravelForWork]:
          editParticipantData?.participantWillingToTravelForWork ?? false,
        [ParticipantTabFormKey.experis]: editParticipantData?.experis ?? false,
        [ParticipantTabFormKey.manpowerEngineering]:
          editParticipantData?.manpowerEngineering ?? false,
        [ParticipantTabFormKey.jeffersonWells]:
          editParticipantData?.jeffersonWells ?? false,
        [ParticipantTabFormKey.rpo]: editParticipantData?.rpo ?? false,

        [CoachAssignmentFormKeys.coachAssignments]: getValueOrBlankArray(
          editParticipantData?.coachAssignment?.coachTypeAssignments,
        ),
        [CoachAssignmentFormKeys.coachAssignmentsId]: getValueOrBlank(
          editParticipantData?.coachAssignment?.coachAssignmentId,
        ),
        [CoachAssignmentFormKeys.toBeAssignDate]: coachToBeAssignCoach,
        isTPSBlacklist: editParticipantData?.isTPSBlacklist,

        [ParticipantTabFormKey.primaryEmail]: getValueOrBlank(
          editParticipantData?.participantsContactDetails?.emailType1?.email,
        ),
        [ParticipantTabFormKey.alternativeEmail]: getValueOrBlank(
          editParticipantData?.participantsContactDetails?.emailType2?.email,
        ),
        [ParticipantTabFormKey.emailType1]: getValueOrBlank(
          editParticipantData?.participantsContactDetails?.emailType1
            ?.emailType,
        ),
        [ParticipantTabFormKey.emailType2]: getValueOrBlank(
          editParticipantData?.participantsContactDetails?.emailType2
            ?.emailType,
        ),
        [ParticipantTabFormKey.primaryNumber]: getValueOrBlank(
          editParticipantData?.participantsContactDetails?.contactType1?.number,
        ),
        [ParticipantTabFormKey.primaryCountryCode]: getValueOrBlank(
          editParticipantData?.participantsContactDetails?.contactType1
            ?.countryCode,
        ),
        [ParticipantTabFormKey.primaryIsoCode]: getValueOrBlank(
          editParticipantData?.participantsContactDetails?.contactType1
            ?.isoCode,
        ),
        [ParticipantTabFormKey.alternativeNumber]: getValueOrBlank(
          editParticipantData?.participantsContactDetails?.contactType2?.number,
        ),
        [ParticipantTabFormKey.alternativeCountryCode]: getValueOrBlank(
          editParticipantData?.participantsContactDetails?.contactType2
            ?.countryCode,
        ),
        [ParticipantTabFormKey.alternativeIsoCode]: getValueOrBlank(
          editParticipantData?.participantsContactDetails?.contactType2
            ?.isoCode,
        ),
        [ParticipantTabFormKey.concatType1]: getValueOrBlank(
          editParticipantData?.participantsContactDetails?.contactType1
            ?.contactType,
        ),
        [ParticipantTabFormKey.concatType2]: getValueOrBlank(
          editParticipantData?.participantsContactDetails?.contactType2
            ?.contactType,
        ),
        [ParticipantTabFormKey.appendPhoneNumber]: getValueOrBlank(
          editParticipantData?.participantsContactDetails?.appendNumber?.number,
        ),
        [ParticipantTabFormKey.appendPhoneNumberCountryCode]: getValueOrBlank(
          editParticipantData?.participantsContactDetails?.appendNumber
            ?.countryCode,
        ),
        [ParticipantTabFormKey.appendPhoneNumberIsoCode]: getValueOrBlank(
          editParticipantData?.participantsContactDetails?.appendNumber
            ?.isoCode,
        ),
        [ParticipantTabFormKey.smsCheckBox]:
          editParticipantData?.nextNotificationPreferences
            ?.isSMSNotificationEnabled,
        [ParticipantTabFormKey.emailCheckBox]:
          editParticipantData?.nextNotificationPreferences
            ?.isEmailNotificationEnabled,
        [ParticipantTabFormKey.nextSms]: getSmsNotification(
          !!editParticipantData?.nextNotificationPreferences
            ?.isSMSNotificationEnabled,
          !!editParticipantData?.nextNotificationPreferences
            ?.isEmailNotificationEnabled,
          getValueOrBlank(
            editParticipantData?.nextNotificationPreferences
              ?.smsNotification?.[0]?.number,
          ),
        ),
        [ParticipantTabFormKey.nextEmail]: getSelectedEmail(
          editParticipantData?.nextNotificationPreferences?.emailNotification,
        ),
        [ParticipantTabFormKey.isPrimaryEmail]: isPrimaryEmail,
        [ParticipantTabFormKey.isNextRegister]:
          editParticipantData?.isRegisteredInNext,
        [ParticipantTabFormKey.batchTypeKey]: getValueOrBlank(
          editParticipantData?.batchTypeKey,
        ),
        [ParticipantTabFormKey.waveCohort]: getValueOrBlank(
          editParticipantData?.waveCohort,
        ),
        [ParticipantTabFormKey.intakeStatusId]: getValueOrBlank(
          editParticipantData?.intakeDetails?.intakeStatusId,
        ),
        [ParticipantTabFormKey.biosRequestedCoachIds]: getIdsAsStringArray(
          editParticipantData?.intakeDetails?.biosRequestedCoach,
        ),
        [ParticipantTabFormKey.biosDeclinedCoachIds]: getIdsAsStringArray(
          editParticipantData?.intakeDetails?.biosDeclinedCoach,
        ),
        [ParticipantTabFormKey.bioStatusId]: getValueOrBlank(
          editParticipantData?.intakeDetails?.bioStatusId,
        ),
        [ParticipantTabFormKey.biosSentCoachIds]: getIdsAsStringArray(
          editParticipantData?.intakeDetails?.biosSentCoach,
        ),

        [ParticipantTabFormKey.chemistryMeetingStatusId]: getValueOrBlank(
          editParticipantData?.intakeDetails?.chemistryMeetingStatusId,
        ),

        [ParticipantTabFormKey.chemistryMeetingSelectedCoachIds]:
          getIdsAsStringArray(
            editParticipantData?.intakeDetails?.chemistryMeetingSelectedCoach,
          ),

        [ParticipantTabFormKey.isCoachSelectionDeclined]:
          !!editParticipantData?.intakeDetails?.isCoachSelectionDeclined,
        [ParticipantTabFormKey.coachSelectedCoachIds]: getValueOrBlank(
          editParticipantData?.intakeDetails?.coachSelectedCoach?.[0]?.id,
        ),
      };

      setParticipantValues(defaultParticipantValues);
      resetParticipant(defaultParticipantValues);

      setIsDataSet(true);

      setTimeout(() => {
        setValue(
          ParticipantTabFormKey.postalCode,
          getValueOrBlank(editParticipantData?.postalCode),
        );
      }, 1000);
    }
  }, [editParticipantData]);

  const permissionParticipantInformation = () => {
    if (isFromCoachPortal) {
      return isEditProgram
        ? MODULE_PERMISSIONS.COACH_PORTAL_EDIT_PARTICIPANT_INFORMATION
        : MODULE_PERMISSIONS.COACH_PORTAL_ADD_PARTICIPANT_INFORMATION;
    }
    return isEditProgram
      ? MODULE_PERMISSIONS.EDIT_PARTICIPANT_INFORMATION
      : MODULE_PERMISSIONS.ADD_PARTICIPANT_INFORMATION;
  };

  const permissionAddressDetails = () => {
    if (isFromCoachPortal) {
      return isEditProgram
        ? MODULE_PERMISSIONS.COACH_PORTAL_EDIT_ADDRESS_DETAILS
        : MODULE_PERMISSIONS.COACH_PORTAL_ADD_ADDRESS_DETAILS;
    }
    return isEditProgram
      ? MODULE_PERMISSIONS.EDIT_ADDRESS_DETAILS
      : MODULE_PERMISSIONS.ADD_ADDRESS_DETAILS;
  };

  const permissionOutreach = () => {
    if (isFromCoachPortal) {
      return isEditProgram
        ? MODULE_PERMISSIONS.COACH_PORTAL_EDIT_OUTREACH
        : MODULE_PERMISSIONS.COACH_PORTAL_ADD_OUTREACH;
    }
    return isEditProgram
      ? MODULE_PERMISSIONS.EDIT_OUTREACH
      : MODULE_PERMISSIONS.ADD_OUTREACH;
  };

  const permissionSponsoringEmployer = () => {
    if (isFromCoachPortal) {
      return isEditProgram
        ? MODULE_PERMISSIONS.COACH_PORTAL_EDIT_SPONSORING_EMPLOYER
        : MODULE_PERMISSIONS.COACH_PORTAL_ADD_SPONSORING_EMPLOYER;
    }
    return isEditProgram
      ? MODULE_PERMISSIONS.EDIT_SPONSORING_EMPLOYER
      : MODULE_PERMISSIONS.ADD_SPONSORING_EMPLOYER;
  };

  return (
    <MuiBox className="participant-tab">
      <MuiBox className="header-collapse">
        <MuiButtonTertiary
          id={COMPONENT_IDS.EXPAND_COLLAPSE_BUTTON}
          onClick={handleExpandCollapseAllChange}
        >
          {accordionStatus.all
            ? commonLabels.collapseAll
            : commonLabels.expandAll}
          {accordionStatus.all ? getRemoveIcon() : getAddIcon()}
        </MuiButtonTertiary>
      </MuiBox>

      {isFromCoachPortal && isNotEmpty(programDetail?.registrationCode) && (
        <AccordionHeader
          wrapperClassName="participant-accordion"
          title={
            <AccordionHeaderText
              label={fetchLabel(
                programSiteCoreKeys.generalDetails,
                programSiteCoreKeys.registrationDetails,
              )}
            />
          }
          onChange={() =>
            handleIndividualAccordionChange("registrationDetails")
          }
          isExpanded={accordionStatus.registrationDetails}
        >
          <MuiBox className="content">
            <RegistrationDetail programDetail={programDetail} />
          </MuiBox>
        </AccordionHeader>
      )}

      <PermissionWrapper permission={permissionParticipantInformation()}>
        <AccordionHeader
          wrapperClassName="participant-accordion"
          title={renderHeader(programSiteCoreKeys.participantInformation)}
          onChange={() =>
            handleIndividualAccordionChange("contactAndNotification")
          }
          isExpanded={accordionStatus.participantInformation}
        >
          <ParticipantInformation
            pageId={pageId}
            programData={programDetail}
            isEditProgram={isEditProgram}
            editProgramParticipantData={participantValues}
            languageOptions={languageOptions}
            isNextRegister={editParticipantData?.isRegisteredInNext as boolean}
          />
        </AccordionHeader>
      </PermissionWrapper>
      <AccordionHeader
        wrapperClassName="participant-accordion"
        title={renderHeader(
          programSiteCoreKeys.contactAndNotificationPreferences,
        )}
        onChange={() =>
          handleIndividualAccordionChange("contactAndNotification")
        }
        isExpanded={accordionStatus.contactAndNotification}
      >
        <ParticipantContactAndNotification
          pageId={pageId}
          isEditProgram={isEditProgram}
          editProgramParticipantData={participantValues}
          isNextRegister={editParticipantData?.isRegisteredInNext}
          smsOptions={smsOptions}
          emailOptions={selectedEmailOptions}
          isSmsSelected={isEnabledSms}
          isEmailSelected={
            editParticipantData?.nextNotificationPreferences
              ?.isEmailNotificationEnabled
          }
        />
      </AccordionHeader>

      <PermissionWrapper permission={permissionAddressDetails()}>
        <AccordionHeader
          wrapperClassName="participant-accordion"
          title={renderHeader(programSiteCoreKeys.addressDetails)}
          onChange={() => handleIndividualAccordionChange("addressDetails")}
          isExpanded={accordionStatus.addressDetails}
        >
          <ParticipantAddressDetails
            pageId={pageId}
            countryVatPostalData={countryVatPostalData}
            isEditProgram={isEditProgram}
            isPendingParticipant={isPending}
            isCityDisable={
              editParticipantData?.isRegisteredInNext &&
              isNotEmpty(editParticipantData?.city)
            }
            deliveryCountry={getValueOrBlank(programDetail?.deliveryCountry)}
          />
        </AccordionHeader>
      </PermissionWrapper>

      <PermissionWrapper permission={permissionOutreach()}>
        <AccordionHeader
          wrapperClassName="participant-accordion"
          title={renderHeader(programSiteCoreKeys.outreach)}
          onChange={() => handleIndividualAccordionChange("outreach")}
          isExpanded={accordionStatus.outreach}
        >
          <ParticipantOutreach
            pageId={pageId}
            programData={programDetail}
            isEditProgram={isEditProgram}
            editParticipantData={participantValues}
            isFromCoachPortal={isFromCoachPortal}
          />
        </AccordionHeader>
      </PermissionWrapper>
      <PermissionWrapper permission={permissionSponsoringEmployer()}>
        <AccordionHeader
          wrapperClassName="participant-accordion"
          title={renderHeader(programSiteCoreKeys.sponsoringEmployer)}
          onChange={() => handleIndividualAccordionChange("sponsoringEmployer")}
          isExpanded={accordionStatus.sponsoringEmployer}
        >
          <ParticipantSponsoringEmployer
            pageId={pageId}
            isEditProgram={isEditProgram}
            programData={programDetail}
          />
        </AccordionHeader>
      </PermissionWrapper>

      {!isFromCoachPortal && isTMLeaderCoachingProgram && (
        <AccordionHeader
          wrapperClassName="participant-accordion"
          title={renderHeader(programSiteCoreKeys.intakeSelection)}
          onChange={() => handleIndividualAccordionChange("intakeSelection")}
          isExpanded={accordionStatus.intakeSelection}
        >
          <ParticipantIntakeSelection
            pageId={pageId}
            intakeDetails={editParticipantData?.intakeDetails}
            isEditProgram={isEditProgram}
            isDataSetProps={isDataSet}
          />
        </AccordionHeader>
      )}

      {!isFromCoachPortal && (
        <PermissionWrapper
          permission={MODULE_PERMISSIONS.EDIT_COACH_ASSIGNMENT}
        >
          <AccordionHeader
            wrapperClassName="participant-accordion"
            title={renderHeader(coachAssignmentSiteCoreKeys.coachAssignment)}
            onChange={() => handleIndividualAccordionChange("coachAssignment")}
            isExpanded={accordionStatus.coachAssignment}
          >
            <CoachAssignment
              pageId={pageId}
              dateFormat={userCountryDateFormat as string}
              isTMIndividualProgram={
                programDetail?.tmProgramType === TM_INDIVIDUAL_ASSESSMENT
              }
              isTMLeaderCoachingProgram={isTMLeaderCoachingProgram}
              onParentSubmitPressed={onParentSubmitPressed}
            />
          </AccordionHeader>
        </PermissionWrapper>
      )}

      {isTMLeaderCoachingProgram && (
        <AccordionHeader
          wrapperClassName="participant-accordion"
          title={renderHeader(goalSelectionSiteCoreKeys.goalSelectionTitle)}
          onChange={() => handleIndividualAccordionChange("goalSelection")}
          isExpanded={accordionStatus.goalSelection}
        >
          <GoalSelection
            pageId={pageId}
            programId={programId}
            participantDetail={editParticipantData}
          />
        </AccordionHeader>
      )}

      {editParticipantData?.isRegisteredInNext && (
        <AccordionHeader
          wrapperClassName="participant-accordion"
          title={renderHeader(programSiteCoreKeys.aboutParticipant)}
          onChange={() => handleIndividualAccordionChange("aboutParticipant")}
          isExpanded={accordionStatus.outreach}
        >
          <AboutParticipant
            pageContentData={pageContentData}
            aboutParticipantNext={editParticipantData?.aboutParticipantNext}
            nextPersona={programDetail?.nextPersona}
            preferredLanguage={editParticipantData?.preferredLanguage}
          />
        </AccordionHeader>
      )}

      {isEditProgram && (
        <PermissionWrapper
          permission={
            isFromCoachPortal
              ? MODULE_PERMISSIONS.COACH_PORTAL_EDIT_CAREER_GOALS
              : MODULE_PERMISSIONS.EDIT_CAREER_GOALS
          }
        >
          <AccordionHeader
            wrapperClassName="participant-accordion"
            title={renderHeader(programSiteCoreKeys.careerGoal)}
            onChange={() => handleIndividualAccordionChange("careerGoal")}
            isExpanded={accordionStatus.careerGoal}
          >
            <ParticipantCareerGoal
              pageId={pageId}
              apiSkills={editParticipantData?.skills ?? []}
              apiTechnicalSkills={editParticipantData?.technicalSkills ?? []}
            />
          </AccordionHeader>
        </PermissionWrapper>
      )}

      {!isFromCoachPortal && (
        <PermissionWrapper
          permission={
            isEditProgram
              ? MODULE_PERMISSIONS.EDIT_PARTICIPANT_ACTIVITY_TIMELINE
              : MODULE_PERMISSIONS.ADD_PARTICIPANT_ACTIVITY_TIMELINE
          }
        >
          <AccordionHeader
            wrapperClassName="participant-accordion"
            title={renderHeader(activityTimelineSiteCoreKeys.activityTimeline)}
            onChange={() =>
              handleIndividualAccordionChange("participantActivityTimeline")
            }
            isExpanded={accordionStatus.participantActivityTimeline}
          >
            <ParticipantActivityTimeline
              pageId={pageId}
              isEditProgram={isEditProgram}
              programId={programId}
              hasDeletePermission={hasModulePermission(
                MODULE_PERMISSIONS.DELETE_PARTICIPANT_ACTIVITY_TIMELINE,
              )}
            />
          </AccordionHeader>
        </PermissionWrapper>
      )}
    </MuiBox>
  );
};
